import { ChevronDown, MapPin, X } from 'lucide-react';
import React from 'react';
import { createPortal } from 'react-dom';
import { useDropdownPosition } from '../../hooks/useDropdownPosition';
import { EventLogLocation } from '../../services/eventLog.service';

type Props = {
  locations: EventLogLocation[];
  selectedLocationIds: number[];
  onChange: (locationIds: number[]) => void;
  loading?: boolean;
};

const LocationFilterDropdown: React.FC<Props> = ({
  locations,
  selectedLocationIds,
  onChange,
  loading,
}) => {
  const { open, setOpen, buttonRef, popoverRef, position } = useDropdownPosition();

  const toggle = (locationId: number) => {
    if (selectedLocationIds.includes(locationId)) {
      onChange(selectedLocationIds.filter(id => id !== locationId));
    } else {
      onChange([...selectedLocationIds, locationId]);
    }
  };

  const buttonLabel = loading
    ? 'Loading...'
    : selectedLocationIds.length === 0
      ? 'All Locations'
      : selectedLocationIds.length === 1
        ? locations.find(l => l.locationId === selectedLocationIds[0])?.locationName || '1 Location'
        : `${selectedLocationIds.length} Locations`;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen(!open)}
        className={`inline-flex h-9 items-center gap-2 rounded-lg border bg-white text-sm px-3 min-w-[140px] justify-between focus:outline-none focus:ring-2 focus:ring-primary-500 hover:shadow-sm transition ${
          selectedLocationIds.length
            ? 'border-primary-400/70 text-gray-900'
            : 'border-gray-300 text-gray-700'
        }`}
      >
        <span className="truncate">{buttonLabel}</span>
        {selectedLocationIds.length > 0 ? (
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              onChange([]);
            }}
            className="text-gray-400 hover:text-gray-600 -mr-1"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-400 flex-shrink-0" />
        )}
      </button>
      {open &&
        createPortal(
          <div
            ref={popoverRef}
            className="w-[260px] max-h-[360px] rounded-xl border border-gray-200 bg-white shadow-lg ring-1 ring-black/5 overflow-hidden"
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              zIndex: 9999,
            }}
          >
            <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-gray-100">
              <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                Locations
              </div>
              {selectedLocationIds.length > 0 && (
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="text-xs font-semibold text-gray-600 hover:text-gray-900"
                >
                  Clear
                </button>
              )}
            </div>
            <div className="overflow-y-auto max-h-[310px] py-1">
              {locations.map(loc => {
                const isSelected = selectedLocationIds.includes(loc.locationId);

                return (
                  <label
                    key={loc.locationId}
                    className={`flex items-center gap-2.5 px-3 py-2 cursor-pointer transition ${
                      isSelected ? 'bg-primary-50' : 'hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggle(loc.locationId)}
                      className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <div
                      className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[10px] ${
                        isSelected ? 'bg-primary-100 text-primary-700' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      <MapPin className="h-3.5 w-3.5" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium truncate">{loc.locationName}</div>
                      <div className="text-[10px] text-gray-400">ID: {loc.locationId}</div>
                    </div>
                  </label>
                );
              })}
              {!locations.length && !loading && (
                <div className="px-3 py-4 text-xs text-gray-400 text-center">
                  No locations found
                </div>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default LocationFilterDropdown;
